import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { Dispatch, SetStateAction } from 'react';
import * as apiProduct from 'api/apiProducts';
import { useAuth } from 'context/AuthContext';
import { hasUserAccess } from 'utils/helpers/access/hasUserAccess';
import { useToast } from 'context/ToastContext';
import { useProduct } from './context/ProductProvider';
import useDeleteProduct from './hooks/hookDeleteProduct';
import ProductFilterStatus from './ProductToolBar/ProductFilterStatus';
import ProductAdminDropdown from './ProductAdminDropdown';
import ListToolBarActions from 'components/toolbar/ListToolBarActions';
import EToolTip from 'components/others/EToolTip';
import { useNeedPay } from 'modules/admin';
import SolarIcon from 'components/icons/SolarIcon';

interface ProductToolBarProps {
    fetchProducts: (force?: boolean) => Promise<unknown>;
    filterStatus: number[];
    setFilterStatus: Dispatch<SetStateAction<number[]>>;
    filterCategoryIds: number[];
    setFilterCategoryIds: (ids: number[]) => void;
}

const ProductToolBar = ({
    fetchProducts,
    filterStatus,
    setFilterStatus,
    filterCategoryIds,
    setFilterCategoryIds,
}: ProductToolBarProps) => {
    const { auth } = useAuth();
    const { t } = useTranslation(['Products', 'common']);
    const { showToast } = useToast();
    const {
        openProductCard,
        productCheck,
        setProductCheck,
        setShowSpinner,
        triggerListReset,
        triggerListRefresh,
        showCategory,
        showId,
        showArticle,
        showUpdated,
        toggleListColumn,
    } = useProduct();
    const { archiveProduct } = useDeleteProduct();
    const { needPay } = useNeedPay();
    const cbArchiveProduct = async () => {
        setShowSpinner(true);
        const resActive = await apiProduct.setStat(productCheck, 0);
        if (!resActive) showToast(t('common:messError'), 'danger');
        await fetchProducts(true);
        setShowSpinner(false);
        setProductCheck([]);
    };

    const handleClickActive = async () => {
        setShowSpinner(true);
        const resActive = await apiProduct.setStat(productCheck, 1);
        if (!resActive) showToast(t('common:messError'), 'danger');
        await fetchProducts(true);
        setShowSpinner(false);
        setProductCheck([]);
    };

    return (
        <div className="mb-3" style={{ minWidth: '100px' }}>
            <div className="page-toolbar-actions">
                {hasUserAccess(auth?.user, 'prodEdit') && (
                    <>
                        <Button
                            className="fs-14"
                            size="sm"
                            variant="success"
                            onClick={(e) => {
                                e.currentTarget.blur();
                                if (needPay()) return;
                                openProductCard(0);
                            }}
                        >
                            <SolarIcon name="plus" className="fs-14 p-0" />
                            <span className="d-none d-md-inline ms-1">{t('toolAddProduct')}</span>
                        </Button>

                        <EToolTip title={t('toolArchive')}>
                            <span className="d-inline-block">
                                <Button
                                    className="fs-14 px-2 toolbar-icon-btn"
                                    size="sm"
                                    variant="danger"
                                    disabled={!productCheck?.length}
                                    onClick={(e) => {
                                        e.currentTarget.blur();
                                        archiveProduct(cbArchiveProduct);
                                    }}
                                >
                                    <SolarIcon name="archive" className="fs-14 p-0" />
                                </Button>
                            </span>
                        </EToolTip>

                        <EToolTip title={t('toolActive')}>
                            <span className="d-inline-block">
                                <Button
                                    className="fs-14 px-2 toolbar-icon-btn"
                                    size="sm"
                                    variant="success"
                                    disabled={!productCheck?.length}
                                    onClick={(e) => {
                                        e.currentTarget.blur();
                                        void handleClickActive();
                                    }}
                                >
                                    <SolarIcon name="check2" className="fs-14 p-0" />
                                </Button>
                            </span>
                        </EToolTip>
                    </>
                )}

                {!!auth?.user?.admin && <ProductAdminDropdown />}

                <ListToolBarActions
                    onReset={triggerListReset}
                    onRefresh={triggerListRefresh}
                    columns={{
                        items: [
                            { id: 'category', label: t('fields.category'), visible: showCategory },
                            { id: 'id', label: t('fields.id'), visible: showId },
                            { id: 'article', label: t('fields.article'), visible: showArticle },
                            { id: 'updated', label: t('fields.updated'), visible: showUpdated },
                        ],
                        onToggle: (id) => toggleListColumn(id as 'category' | 'id' | 'article' | 'updated'),
                    }}
                />

                <ProductFilterStatus
                    filterStatus={filterStatus}
                    setFilterStatus={setFilterStatus}
                    filterCategoryIds={filterCategoryIds}
                    setFilterCategoryIds={setFilterCategoryIds}
                />
            </div>

        </div>
    );
};

export default ProductToolBar;
